/**
 * Quest Editor Component
 * Full-featured form for creating and editing quests
 */

import { useState, useEffect } from 'react';
import { QuestDefinition, QuestType, QuestObjectiveType, QuestRewardType, QuestObjective, QuestReward, Quality } from '@/types';
import { customQuestManager, questChainManager, marketManager } from '@/systems';

interface QuestEditorProps {
  quest: QuestDefinition | null;
  onSave: () => void;
  onCancel: () => void;
}

export function QuestEditor({ quest, onSave, onCancel }: QuestEditorProps) {
  // Form state
  const [questId, setQuestId] = useState('');
  const [questName, setQuestName] = useState('');
  const [questDescription, setQuestDescription] = useState('');
  const [questType, setQuestType] = useState<QuestType>(QuestType.CATCH_QUANTITY);
  const [category, setCategory] = useState('custom');
  const [chainId, setChainId] = useState<string | null>(null);
  const [isTutorial, setIsTutorial] = useState(false);
  const [repeatable, setRepeatable] = useState(false);
  const [objectives, setObjectives] = useState<QuestObjective[]>([]);
  const [rewards, setRewards] = useState<QuestReward[]>([]);
  const [unlockConditions, setUnlockConditions] = useState<any>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showExportCode, setShowExportCode] = useState(false);

  // Load quest data when editing
  useEffect(() => {
    if (quest) {
      setQuestId(quest.id);
      setQuestName(quest.name);
      setQuestDescription(quest.description);
      setQuestType(quest.type);
      setCategory(quest.category || 'custom');
      setChainId(quest.chainId || null);
      setIsTutorial(quest.isTutorial ?? false);
      setRepeatable(quest.repeatable ?? false);
      setObjectives(quest.objectives);
      setRewards(quest.rewards);
      setUnlockConditions(quest.unlockConditions || {});
    } else {
      // New quest - generate ID
      const newId = customQuestManager.generateQuestId('new_quest');
      setQuestId(newId);
    }
  }, [quest]);

  // Add new objective
  const handleAddObjective = () => {
    const newObjective: QuestObjective = {
      id: `obj_${objectives.length + 1}`,
      type: QuestObjectiveType.CATCH_FISH,
      description: '',
      targetValue: 1,
    };
    setObjectives([...objectives, newObjective]);
  };

  // Remove objective
  const handleRemoveObjective = (index: number) => {
    setObjectives(objectives.filter((_, i) => i !== index));
  };

  // Update objective field
  const handleUpdateObjective = (index: number, field: keyof QuestObjective, value: any) => {
    const updated = [...objectives];
    updated[index] = { ...updated[index], [field]: value };
    setObjectives(updated);
  };

  // Add objective filter
  const handleAddObjectiveFilter = (index: number, filterKey: string, value: any) => {
    const updated = [...objectives];
    const filters = updated[index].filters || {};
    updated[index] = {
      ...updated[index],
      filters: { ...filters, [filterKey]: value },
    };
    setObjectives(updated);
  };

  // Add new reward
  const handleAddReward = () => {
    const newReward: QuestReward = {
      type: QuestRewardType.CURRENCY,
      value: 0,
      description: '',
    };
    setRewards([...rewards, newReward]);
  };

  // Remove reward
  const handleRemoveReward = (index: number) => {
    setRewards(rewards.filter((_, i) => i !== index));
  };

  // Update reward field
  const handleUpdateReward = (index: number, field: keyof QuestReward, value: any) => {
    const updated = [...rewards];
    updated[index] = { ...updated[index], [field]: value };
    setRewards(updated);
  };

  // Handle save
  const handleSave = () => {
    const questData: QuestDefinition = {
      id: questId,
      name: questName,
      description: questDescription,
      type: questType,
      category,
      chainId: chainId || undefined,
      isTutorial,
      repeatable,
      objectives,
      rewards,
      unlockConditions,
    };

    // Validate quest
    const errors = customQuestManager.validateQuest(questData);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Save quest
    customQuestManager.saveQuest(questData);
    marketManager.reloadAllQuests();
    setValidationErrors([]);
    onSave();
  };

  // Handle delete
  const handleDelete = () => {
    if (!quest) return;
    if (confirm(`Delete quest "${quest.name}"? This cannot be undone.`)) {
      customQuestManager.deleteQuest(quest.id);
      marketManager.reloadAllQuests();
      onCancel();
    }
  };

  // Handle export
  const handleExport = () => {
    setShowExportCode(true);
  };

  const exportCode = quest ? customQuestManager.exportQuestAsTypeScript(quest) : '';

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-700">
        <h3 className="text-lg font-bold text-white">
          {quest ? 'Edit Quest' : 'New Quest'}
        </h3>
        <div className="flex gap-2">
          {quest && customQuestManager.isCustomQuest(quest.id) && (
            <button
              onClick={handleDelete}
              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-bold transition-colors"
            >
              Delete
            </button>
          )}
          {quest && (
            <button
              onClick={handleExport}
              className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm font-bold transition-colors"
            >
              Export
            </button>
          )}
          <button
            onClick={onCancel}
            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-bold transition-colors"
          >
            Save Quest
          </button>
        </div>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-600 rounded">
          <p className="text-red-400 font-bold text-sm mb-1">Validation Errors:</p>
          <ul className="text-red-300 text-xs space-y-1">
            {validationErrors.map((error, idx) => (
              <li key={idx}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Export Code Modal */}
      {showExportCode && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-6">
          <div className="bg-gray-900 border-2 border-purple-500 rounded-xl p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Export as TypeScript</h3>
              <button
                onClick={() => setShowExportCode(false)}
                className="text-gray-400 hover:text-white text-xl"
              >
                ✕
              </button>
            </div>
            <pre className="bg-gray-950 p-4 rounded text-xs text-green-400 overflow-x-auto">
              {exportCode}
            </pre>
            <button
              onClick={() => {
                navigator.clipboard.writeText(exportCode);
                alert('Copied to clipboard!');
              }}
              className="mt-4 w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded font-bold transition-colors"
            >
              Copy to Clipboard
            </button>
          </div>
        </div>
      )}

      {/* Form Content - Scrollable */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-2">
        {/* Basic Info Section */}
        <section className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <h4 className="font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-blue-400">📝</span> Basic Information
          </h4>

          <div className="space-y-3">
            {/* Quest ID */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Quest ID</label>
              <input
                type="text"
                value={questId}
                onChange={(e) => setQuestId(e.target.value)}
                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                placeholder="custom_my_quest"
              />
              <p className="text-xs text-gray-500 mt-1">Unique identifier (lowercase, underscores only)</p>
            </div>

            {/* Quest Name */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Quest Name</label>
              <input
                type="text"
                value={questName}
                onChange={(e) => setQuestName(e.target.value)}
                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                placeholder="My Awesome Quest"
              />
            </div>

            {/* Quest Description */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Description</label>
              <textarea
                value={questDescription}
                onChange={(e) => setQuestDescription(e.target.value)}
                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                rows={3}
                placeholder="Quest description that players will see..."
              />
            </div>

            {/* Quest Type */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Quest Type</label>
              <select
                value={questType}
                onChange={(e) => setQuestType(e.target.value as QuestType)}
                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
              >
                <optgroup label="Catch Quests">
                  <option value={QuestType.CATCH_QUANTITY}>Catch Quantity</option>
                  <option value={QuestType.CATCH_SPECIES}>Catch Specific Species</option>
                  <option value={QuestType.CATCH_QUALITY}>Catch Quality</option>
                  <option value={QuestType.CATCH_SIZE}>Catch Size</option>
                  <option value={QuestType.CATCH_RARITY}>Catch Rarity</option>
                </optgroup>
                <optgroup label="Selling Quests">
                  <option value={QuestType.SELL_SPECIES}>Sell Species</option>
                  <option value={QuestType.SELL_VALUE}>Sell Value</option>
                  <option value={QuestType.SELL_QUALITY}>Sell Quality</option>
                </optgroup>
                <optgroup label="Progression Quests">
                  <option value={QuestType.EARN_CURRENCY}>Earn Currency</option>
                  <option value={QuestType.SPEND_CURRENCY}>Spend Currency</option>
                  <option value={QuestType.UNLOCK_EQUIPMENT}>Unlock Equipment</option>
                  <option value={QuestType.UNLOCK_BUDDY}>Unlock Buddy</option>
                </optgroup>
                <optgroup label="Exploration Quests">
                  <option value={QuestType.VISIT_ZONE}>Visit Zone</option>
                  <option value={QuestType.FISH_IN_ZONE}>Fish in Zone</option>
                </optgroup>
                <optgroup label="Time-Based Quests">
                  <option value={QuestType.DAILY_CATCH}>Daily Catch</option>
                  <option value={QuestType.TIMED_CHALLENGE}>Timed Challenge</option>
                </optgroup>
                <optgroup label="Collection Quests">
                  <option value={QuestType.DISCOVER_SPECIES}>Discover Species</option>
                  <option value={QuestType.COMPLETE_COLLECTION}>Complete Collection</option>
                </optgroup>
              </select>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="custom">Custom</option>
                <option value="tutorial">Tutorial</option>
                <option value="progression">Progression</option>
                <option value="daily">Daily</option>
                <option value="challenge">Challenge</option>
                <option value="bounty">Bounty</option>
                <option value="buddy_quest">Buddy Quest</option>
              </select>
            </div>

            {/* Quest Chain */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Quest Chain
                <span className="text-gray-500 font-normal ml-2 text-xs">
                  Organize quests into folders
                </span>
              </label>
              <select
                value={chainId || ''}
                onChange={(e) => setChainId(e.target.value || null)}
                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="">📋 Uncategorized</option>
                {questChainManager.getAllChains().map(chain => (
                  <option key={chain.id} value={chain.id}>
                    {chain.icon ? `${chain.icon} ` : ''}
                    {chain.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Group related quests together. Create chains in the quest list panel.
              </p>
            </div>

            {/* Flags */}
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isTutorial}
                  onChange={(e) => setIsTutorial(e.target.checked)}
                  className="w-4 h-4"
                />
                Tutorial Quest
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={repeatable}
                  onChange={(e) => setRepeatable(e.target.checked)}
                  className="w-4 h-4"
                />
                Repeatable
              </label>
            </div>
          </div>
        </section>

        {/* Objectives Section */}
        <section className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-white flex items-center gap-2">
              <span className="text-green-400">🎯</span> Objectives
            </h4>
            <button
              onClick={handleAddObjective}
              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-bold transition-colors"
            >
              + Add Objective
            </button>
          </div>

          {objectives.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">No objectives yet. Add one to get started!</p>
          )}

          <div className="space-y-3">
            {objectives.map((objective, idx) => (
              <div key={idx} className="bg-gray-900 rounded p-3 border border-gray-700">
                <div className="flex items-start justify-between mb-2">
                  <span className="text-xs font-bold text-gray-400">Objective {idx + 1}</span>
                  <button
                    onClick={() => handleRemoveObjective(idx)}
                    className="text-red-400 hover:text-red-300 text-xs"
                  >
                    Remove
                  </button>
                </div>

                <div className="space-y-2">
                  {/* Objective ID */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Objective ID</label>
                    <input
                      type="text"
                      value={objective.id}
                      onChange={(e) => handleUpdateObjective(idx, 'id', e.target.value)}
                      className="w-full bg-gray-950 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-green-500"
                      placeholder="obj_1"
                    />
                  </div>

                  {/* Objective Type */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Type</label>
                    <select
                      value={objective.type}
                      onChange={(e) => handleUpdateObjective(idx, 'type', e.target.value)}
                      className="w-full bg-gray-950 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-green-500"
                    >
                      <optgroup label="Player Actions">
                        <option value={QuestObjectiveType.CATCH_FISH}>Catch Fish</option>
                        <option value={QuestObjectiveType.SELL_FISH}>Sell Fish</option>
                        <option value={QuestObjectiveType.EARN_CURRENCY}>Earn Currency</option>
                        <option value={QuestObjectiveType.EARN_GOLD}>Earn Gold</option>
                        <option value={QuestObjectiveType.SPEND_GOLD}>Spend Gold</option>
                        <option value={QuestObjectiveType.BUY_EQUIPMENT}>Buy Equipment</option>
                        <option value={QuestObjectiveType.UNLOCK_ITEM}>Unlock Item</option>
                        <option value={QuestObjectiveType.HIRE_BUDDY}>Hire Buddy</option>
                        <option value={QuestObjectiveType.VISIT_LOCATION}>Visit Location</option>
                        <option value={QuestObjectiveType.REACH_MILESTONE}>Reach Milestone</option>
                      </optgroup>
                      <optgroup label="Buddy Actions">
                        <option value={QuestObjectiveType.BUDDY_CATCH_FISH}>Buddy Catch Fish</option>
                        <option value={QuestObjectiveType.HAULER_SELL_FISH}>Hauler Sell Fish</option>
                        <option value={QuestObjectiveType.HAULER_EARN_GOLD}>Hauler Earn Gold</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Description</label>
                    <input
                      type="text"
                      value={objective.description}
                      onChange={(e) => handleUpdateObjective(idx, 'description', e.target.value)}
                      className="w-full bg-gray-950 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-green-500"
                      placeholder="Catch 10 fish"
                    />
                  </div>

                  {/* Target Value */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Target Value</label>
                    <input
                      type="number"
                      value={objective.targetValue}
                      onChange={(e) => handleUpdateObjective(idx, 'targetValue', parseInt(e.target.value) || 0)}
                      className="w-full bg-gray-950 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-green-500"
                      min="1"
                    />
                  </div>

                  {/* Filters */}
                  <div className="border-t border-gray-700 pt-2 mt-2">
                    <p className="text-xs text-gray-500 mb-2">Filters (optional):</p>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Species ID (e.g., bluegill)</label>
                        <input
                          type="text"
                          value={objective.filters?.speciesId || ''}
                          onChange={(e) => handleAddObjectiveFilter(idx, 'speciesId', e.target.value || undefined)}
                          className="w-full bg-gray-950 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-green-500"
                          placeholder="Leave empty for any species"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Min Quality</label>
                        <select
                          value={objective.filters?.quality || ''}
                          onChange={(e) => handleAddObjectiveFilter(idx, 'quality', e.target.value || undefined)}
                          className="w-full bg-gray-950 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-green-500"
                        >
                          <option value="">Any Quality</option>
                          <option value={Quality.COMMON}>Common</option>
                          <option value={Quality.UNCOMMON}>Uncommon</option>
                          <option value={Quality.RARE}>Rare</option>
                          <option value={Quality.EPIC}>Epic</option>
                          <option value={Quality.LEGENDARY}>Legendary</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Rewards Section */}
        <section className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-white flex items-center gap-2">
              <span className="text-yellow-400">🎁</span> Rewards
            </h4>
            <button
              onClick={handleAddReward}
              className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-xs font-bold transition-colors"
            >
              + Add Reward
            </button>
          </div>

          {rewards.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">No rewards yet. Add one to incentivize players!</p>
          )}

          <div className="space-y-3">
            {rewards.map((reward, idx) => (
              <div key={idx} className="bg-gray-900 rounded p-3 border border-gray-700">
                <div className="flex items-start justify-between mb-2">
                  <span className="text-xs font-bold text-gray-400">Reward {idx + 1}</span>
                  <button
                    onClick={() => handleRemoveReward(idx)}
                    className="text-red-400 hover:text-red-300 text-xs"
                  >
                    Remove
                  </button>
                </div>

                <div className="space-y-2">
                  {/* Reward Type */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Type</label>
                    <select
                      value={reward.type}
                      onChange={(e) => handleUpdateReward(idx, 'type', e.target.value)}
                      className="w-full bg-gray-950 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-yellow-500"
                    >
                      <option value={QuestRewardType.CURRENCY}>Currency (Gold)</option>
                      <option value={QuestRewardType.EXPERIENCE}>Experience (XP)</option>
                      <option value={QuestRewardType.INVENTORY_EXPANSION}>Inventory Expansion</option>
                      <option value={QuestRewardType.EQUIPMENT}>Equipment</option>
                      <option value={QuestRewardType.UNLOCK_ZONE}>Unlock Zone</option>
                      <option value={QuestRewardType.UNLOCK_SHOP}>Unlock Shop</option>
                      <option value={QuestRewardType.UNLOCK_BUDDY}>Unlock Buddy</option>
                    </select>
                  </div>

                  {/* Value */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Value</label>
                    <input
                      type="number"
                      value={reward.value}
                      onChange={(e) => handleUpdateReward(idx, 'value', parseInt(e.target.value) || 0)}
                      className="w-full bg-gray-950 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-yellow-500"
                      min="0"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Description</label>
                    <input
                      type="text"
                      value={reward.description}
                      onChange={(e) => handleUpdateReward(idx, 'description', e.target.value)}
                      className="w-full bg-gray-950 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-yellow-500"
                      placeholder="+100 gold"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Unlock Conditions Section */}
        <section className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <h4 className="font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-purple-400">🔒</span> Unlock Conditions
          </h4>

          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Min Currency</label>
              <input
                type="number"
                value={unlockConditions.minCurrency || ''}
                onChange={(e) => setUnlockConditions({
                  ...unlockConditions,
                  minCurrency: e.target.value ? parseInt(e.target.value) : undefined,
                })}
                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                placeholder="0 (no requirement)"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Min Level</label>
              <input
                type="number"
                value={unlockConditions.minLevel || ''}
                onChange={(e) => setUnlockConditions({
                  ...unlockConditions,
                  minLevel: e.target.value ? parseInt(e.target.value) : undefined,
                })}
                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                placeholder="0 (no requirement)"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Required Completed Quests (comma-separated IDs)</label>
              <input
                type="text"
                value={unlockConditions.completedQuests?.join(', ') || ''}
                onChange={(e) => setUnlockConditions({
                  ...unlockConditions,
                  completedQuests: e.target.value ? e.target.value.split(',').map(s => s.trim()).filter(Boolean) : undefined,
                })}
                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                placeholder="tut_first_cast, tut_first_sale"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
